import React, { useState, useEffect, useRef, useContext } from 'react';
import styles from './EBBMenu.module.css';
import { useAppContext } from "@components/Provider";
import Image from 'next/image';
import Link from 'next/link';

const splitIntoColumns = (items, numColumns = 4) => {
    // Create rows with exactly 4 columns each
    const rows = [];
    const itemsPerRow = numColumns;
    
    for (let i = 0; i < items.length; i += itemsPerRow) {
        const row = items.slice(i, i + itemsPerRow);
        // Ensure each row has exactly 4 columns by padding with null if needed
        while (row.length < itemsPerRow) {
            row.push(null); // Add null to maintain 4-column structure
        }
        rows.push(row);
    }
    
    return rows;
};

const MegaMenu = ({ subMenus, className, onLinkClick }) => {
    const rows = splitIntoColumns(subMenus);
    const columnRefs = useRef([]);
    const [minHeight, setMinHeight] = useState(0);

    // Custom click handler for server-side navigation
    const handleServerSideClick = (e, url, isExternal) => {
        if (isExternal) {
            return; // Let external links work normally
        }
        
        e.preventDefault(); // Prevent default Link behavior
        onLinkClick && onLinkClick(); // Close menu if needed
        
        // Force server-side navigation by using window.location
        window.location.href = url;
    };

    useEffect(() => {
        // After render, set all columns to the height of the tallest column
        const colEls = columnRefs.current.filter(Boolean);
        if (!colEls.length) return;
        
        // Reset heights to auto to measure natural height
        colEls.forEach(col => col.style.height = 'auto');
        
        // Find the tallest column height
        const maxHeight = Math.max(...colEls.map(col => col.offsetHeight));
        
        // Set all columns to the same height for consistency
        colEls.forEach(col => {
            col.style.height = `${maxHeight}px`;
        });
        
        setMinHeight(maxHeight);
    }, [subMenus.length, rows.length, subMenus]);

    return (
        <div className={`${styles.megaMenu} ${className}`}>
            <div className={styles.megaMenuInner}>
                {rows.map((row, rowIdx) => (
                    <div key={rowIdx} className={styles.megaMenuRow}>
                        {row.map((group, colIdx) => (
                            group ? (
                                <div
                                    key={`${rowIdx}-${colIdx}`}
                                    className={styles.megaMenuColumn}
                                    ref={el => columnRefs.current[`${rowIdx}-${colIdx}`] = el}
                                >
                                    <div className={styles.megaMenuGroup} style={{ minHeight: `${minHeight - 200}px` }}>
                                        <div className={styles.megaMenuGroupHeader}>
                                            {group.imageUrl && (
                                                <Image
                                                    src={group.imageUrl}
                                                    alt={group.text}
                                                    width={20}
                                                    height={20}
                                                    className={styles.menuIcon}
                                                />
                                            )}
                                            <span>{group.text}</span>
                                        </div>
                                        <div className={styles.megaMenuChildren}>
                                            {group.subMenus?.length > 0 ? (
                                                group.subMenus.map((child, cidx) => (
                                                    <Link
                                                        key={cidx}
                                                        href={child.url}
                                                        className={styles.megaMenuChild}
                                                        target={child.external ? "_blank" : "_self"}
                                                        rel={child.external ? "noopener noreferrer" : "noopener"}
                                                        onClick={(e) => handleServerSideClick(e, child.url, child.external)}
                                                    >
                                                        {child.text}
                                                    </Link>
                                                ))
                                            ) : group.children?.length > 0 ? (
                                                group.children.map((child, cidx) => (
                                                    <Link
                                                        key={cidx}
                                                        href={child.url}
                                                        className={styles.megaMenuChild}
                                                        target={child.external ? "_blank" : "_self"}
                                                        rel={child.external ? "noopener noreferrer" : "noopener"}
                                                        onClick={(e) => handleServerSideClick(e, child.url, child.external)}
                                                    >
                                                        {child.text}
                                                    </Link>
                                                ))
                                            ) : null}
                                        </div>
                                        {/* Add bottom spacing for last row */}
                                        {rowIdx === rows.length - 1 && <div style={{ height: '2rem' }}></div>}
                                    </div>
                                </div>
                            ) : (
                                <div key={`${rowIdx}-${colIdx}`} className={`${styles.megaMenuColumn} ${styles.emptyColumn}`}></div>
                            )
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const NestedSubMenu = ({ subMenus, onLinkClick }) => {
    // Custom click handler for server-side navigation
    const handleServerSideClick = (e, url, isExternal) => {
        if (isExternal) {
            return; // Let external links work normally
        }
        
        e.preventDefault(); // Prevent default Link behavior
        onLinkClick && onLinkClick(); // Close menu if needed
        
        // Force server-side navigation by using window.location
        window.location.href = url;
    };

    return (
        <div className={styles.nestedSubMenu}>
            {subMenus.map((nestedItem, nestedIndex) => (
                <div
                    key={nestedIndex}
                    className={styles.nestedMenuItemContainer}
                >
                    <Link
                        href={nestedItem.url}
                        className={styles.nestedMenuItem}
                        target={nestedItem.external ? "_blank" : "_self"}
                        rel={nestedItem.external ? "noopener noreferrer" : "noopener"}
                        onClick={(e) => handleServerSideClick(e, nestedItem.url, nestedItem.external)}
                    >
                        {nestedItem.imageUrl && (
                            <Image
                                src={nestedItem.imageUrl}
                                alt={nestedItem.text}
                                width={20}
                                height={20}
                                className={styles.menuIcon}
                            />
                        )}
                        {nestedItem.text}
                    </Link>
                    {nestedItem.children && nestedItem.children.length > 0 && (
                        <NestedSubMenu subMenus={nestedItem.children} onLinkClick={onLinkClick} />
                    )}
                </div>
            ))}
        </div>
    );
};

const SubMenu = ({ subMenus, isMegaMenu, className, parentItem, onLinkClick }) => {
    // Custom click handler for server-side navigation
    const handleServerSideClick = (e, url, isExternal) => {
        if (isExternal) {
            return; // Let external links work normally
        }
        
        e.preventDefault(); // Prevent default Link behavior
        onLinkClick && onLinkClick(); // Close menu if needed
        
        // Force server-side navigation by using window.location
        window.location.href = url;
    };

    if (isMegaMenu) {
        return <MegaMenu subMenus={subMenus} className={className} onLinkClick={onLinkClick} />;
    }
    return (
        <div className={`${styles.subMenu} ${className}`}>
            {subMenus.map((subItem, subIndex) => (
                <div
                    key={subIndex}
                    className={styles.subMenuItemContainer}
                >
                    <Link
                        href={subItem.url}
                        className={styles.subMenuItem}
                        target={subItem.external ? "_blank" : "_self"}
                        rel={subItem.external ? "noopener noreferrer" : "noopener"}
                        onClick={(e) => handleServerSideClick(e, subItem.url, subItem.external)}
                    >
                        {subItem.imageUrl && (
                            <Image
                                src={subItem.imageUrl}
                                alt={subItem.text}
                                width={20}
                                height={20}
                                className={styles.menuIcon}
                            />
                        )}
                        {subItem.text}
                    </Link>
                    {subItem.children && subItem.children.length > 0 && (
                        <NestedSubMenu subMenus={subItem.children} onLinkClick={onLinkClick} />
                    )}
                </div>
            ))}
        </div>
    );
};

const EBBMenu = () => {
    const { data } = useAppContext();
    const [activeMenuIndex, setActiveMenuIndex] = useState(null);
    const menuRef = useRef(null);

    // Custom click handler for server-side navigation
    const handleServerSideClick = (e, url, isExternal) => {
        if (isExternal) {
            return; // Let external links work normally
        }
        
        e.preventDefault(); // Prevent default Link behavior
        closeMenu(); // Close menu
        
        // Force server-side navigation by using window.location
        window.location.href = url;
    };

    const closeMenu = () => {
        setActiveMenuIndex(null);
    };

    useEffect(() => {
        if (activeMenuIndex !== null) {
            document.body.classList.add(styles.menuOpen);
        } else {
            document.body.classList.remove(styles.menuOpen);
        }

        return () => {
            document.body.classList.remove(styles.menuOpen);
        };
    }, [activeMenuIndex]);

    const handleMenuClick = (index) => {
        setActiveMenuIndex(activeMenuIndex === index ? null : index);
    };

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setActiveMenuIndex(null);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={`${styles.ebbMenu} ${activeMenuIndex !== null ? styles.active : ''}`} ref={menuRef}>
            {data?.SiteSettings?.site_header?.nav_items?.map((item, index) => {
                // Check if menu has 3 levels of nesting instead of hardcoded text comparison
                const hasThreeLevels = item?.subMenus?.some(subMenu =>
                    subMenu?.subMenus?.length > 0 || subMenu?.children?.length > 0
                );
                const hasSubmenu = item?.subMenus?.length > 0;
                return (
                    <div
                        key={index}
                        className={`${styles.menuItem} ${hasSubmenu ? styles.hasSubmenu : ''} ${activeMenuIndex === index ? styles.active : ''}`}
                    >
                        {hasSubmenu ? (
                            <span
                                className={styles.menuLink}
                                onClick={() => handleMenuClick(index)}
                            >
                                {item?.imageUrl && (
                                    <Image
                                        src={item.imageUrl}
                                        alt={item?.text || ''}
                                        width={20}
                                        height={20}
                                        className={styles.menuIcon}
                                    />
                                )}
                                {item?.text}
                                <svg
                                    className={styles.dropdownArrow}
                                    style={{
                                        transform: activeMenuIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                    }}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </span>
                        ) : (
                            <Link
                                href={item?.url}
                                className={styles.menuLink}
                                target={item?.external ? "_blank" : "_self"}
                                rel={item?.external ? "noopener noreferrer" : "noopener"}
                                onClick={(e) => handleServerSideClick(e, item?.url, item?.external)}
                            >
                                {item?.imageUrl && (
                                    <Image
                                        src={item.imageUrl}
                                        alt={item?.text || ''}
                                        width={20}
                                        height={20}
                                        className={styles.menuIcon}
                                    />
                                )}
                                {item?.text}
                            </Link>
                        )}
                    </div>
                );
            })}
            
            {/* Render dropdowns as children of the main menu container */}
            {activeMenuIndex !== null && data?.SiteSettings?.site_header?.nav_items?.[activeMenuIndex] && (
                <SubMenu
                    subMenus={data.SiteSettings.site_header.nav_items[activeMenuIndex].subMenus}
                    isMegaMenu={data.SiteSettings.site_header.nav_items[activeMenuIndex]?.subMenus?.some(subMenu =>
                        subMenu?.subMenus?.length > 0 || subMenu?.children?.length > 0
                    )}
                    className={styles.active}
                    parentItem={data.SiteSettings.site_header.nav_items[activeMenuIndex]}
                    onLinkClick={closeMenu}
                />
            )}
        </div>
    );
};

export default EBBMenu;
